import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  UserProfileDto,
  UserRole,
  UpdateQuestionDto,
  QuestionDto,
} from "@green0meter/api-client";

import { DOMAIN_DOUBLE_MATERIALITY } from "../domain";

import { roleGuard } from "@api";
import { doubleMaterialityQuestionsApi } from "@api/services";

const allowedRoles = [UserRole.Manager, UserRole.Owner];
const ACTION_GET_QUESTIONS = "GET_QUESTIONS";

const updateQuestionFn = async ({
  id,
  payload,
}: {
  id: string;
  payload: UpdateQuestionDto;
}): Promise<QuestionDto> => {
  const res =
    await doubleMaterialityQuestionsApi.doubleMaterialityQuestionsControllerUpdateQuestion(
      id,
      payload,
    );

  return res.data;
};

export const useUpdateQuestion = (
  user: UserProfileDto | null,
): {
  updateQuestion: (id: string, payload: UpdateQuestionDto) => void;
  updateQuestionAsync: (id: string, payload: UpdateQuestionDto) => Promise<unknown>;
  isPendingUpdateQuestion: boolean;
  updateQuestionEnabled: boolean;
} => {
  const enabled = roleGuard(user, allowedRoles);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: enabled ? updateQuestionFn : undefined,
    onSuccess: (updatedQuestion) => {
      const baseKey = [DOMAIN_DOUBLE_MATERIALITY, ACTION_GET_QUESTIONS] as const;
      const allQueries = queryClient.getQueriesData<{
        data: QuestionDto[];
        totalItems: number;
        pageNumber: number;
        pageSize: number;
      }>({ queryKey: baseKey });

      allQueries.forEach(([key, current]) => {
        if (!current) return;
        queryClient.setQueryData(key, () => {
          return {
            ...current,
            data: current.data.map((q) =>
              q.id === updatedQuestion.id ? updatedQuestion : q,
            ),
          };
        });
      });
    },
  });

  return {
    updateQuestion: (id, payload) => mutation.mutate({ id, payload }),
    updateQuestionAsync: (id, payload) => mutation.mutateAsync({ id, payload }),
    isPendingUpdateQuestion: mutation.isPending,
    updateQuestionEnabled: enabled,
  };
};
